/**
 * ParcelleMap — carte Leaflet affichant une parcelle cadastrale.
 * Fond de plan IGN Plan (Géoplateforme, WMTS) + contour de la parcelle (GeoJSON
 * renvoyé par l'API Carto IGN). Aucune clé requise. Wrapper léger autour de
 * Leaflet (pas de react-leaflet, pour éviter le couplage de versions).
 */

import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface Props {
  geojson: unknown | null;
  centroide: { lat: number; lng: number } | null;
  height?: number;
}

// Fond IGN Plan v2 (Géoplateforme, gratuit, sans clé).
const IGN_TILES = 'https://data.geopf.fr/wmts?SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=GEOGRAPHICALGRIDSYSTEMS.PLANIGNV2&STYLE=normal&TILEMATRIXSET=PM&FORMAT=image/png&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}';
const IGN_ATTRIB = '© IGN — Géoplateforme';

export function ParcelleMap({ geojson, centroide, height = 280 }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = L.map(ref.current, { attributionControl: true, zoomControl: true });
    L.tileLayer(IGN_TILES, { attribution: IGN_ATTRIB, maxZoom: 19 }).addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Nettoie les anciennes couches vectorielles.
    map.eachLayer((layer) => { if ((layer as L.GeoJSON).feature || layer instanceof L.GeoJSON) map.removeLayer(layer); });

    if (geojson) {
      const gj = L.geoJSON(geojson as GeoJSON.GeoJsonObject, {
        style: { color: 'oklch(0.45 0.155 145)', weight: 2.5, fillColor: 'oklch(0.55 0.16 145)', fillOpacity: 0.22 },
      }).addTo(map);
      try { map.fitBounds(gj.getBounds(), { padding: [24, 24], maxZoom: 19 }); }
      catch { if (centroide) map.setView([centroide.lat, centroide.lng], 18); }
    } else if (centroide) {
      map.setView([centroide.lat, centroide.lng], 18);
    } else {
      map.setView([46.6, 2.5], 5); // France par défaut
    }
    setTimeout(() => map.invalidateSize(), 60);
  }, [geojson, centroide]);

  return <div ref={ref} style={{ height, borderRadius: 9, overflow: 'hidden' }} className="border border-border-strong" />;
}
